import type { ReactNode } from 'react';

export interface DotProgressProps {
  readonly size?: number;
  readonly gap?: number;
  readonly count?: number;
  readonly colorVar?: string;
  readonly className?: string;
}

/**
 * Pixel-matrix progress indicator. Five dots running a wave of opacity
 * from left to right — fits the same dot-grid language as the tab-bar
 * icons, Send/Receive buttons, and the new WalletGlyph.
 */
export function DotProgress({
  size = 8,
  gap = 6,
  count = 5,
  colorVar = 'var(--color-pos)',
  className = '',
}: DotProgressProps): ReactNode {
  const cycleMs = 1200;
  const stepMs = cycleMs / count;
  return (
    <div
      className={['inline-flex items-center', className].filter(Boolean).join(' ')}
      style={{ gap }}
      role="status"
      aria-live="polite"
    >
      {Array.from({ length: count }, (_, i) => (
        <span
          key={i}
          aria-hidden
          className="block rounded-full"
          style={{
            width: size,
            height: size,
            background: colorVar,
            opacity: 0.2,
            animation: `gk-dot-pulse ${cycleMs}ms ease-in-out ${i * stepMs}ms infinite`,
          }}
        />
      ))}
      <style>{`
        @keyframes gk-dot-pulse {
          0%, 100% { opacity: 0.15; transform: scale(0.85); }
          35%      { opacity: 1;    transform: scale(1.0);  }
          70%      { opacity: 0.35; transform: scale(0.9);  }
        }
      `}</style>
    </div>
  );
}
